import type { Link } from "@dropin/shared/schemas";

const MAX_LINKS = 20;

export function LinksEditor({
  value,
  onChange,
  testid = "links-editor",
}: {
  value: Link[];
  onChange: (next: Link[]) => void;
  testid?: string;
}) {
  function update(i: number, patch: Partial<Link>) {
    onChange(value.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function add() {
    if (value.length >= MAX_LINKS) return;
    onChange([...value, { label: "", url: "" }]);
  }

  return (
    <div className="grid gap-2.5">
      {value.length === 0 && (
        <p className="text-xs text-muted">No links yet — add your site, Substack, Patreon, anywhere else.</p>
      )}

      {value.map((l, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_auto] sm:grid-cols-[180px_1fr_auto] gap-2 items-stretch rounded-md border border-border bg-paper p-2"
          data-testid={`${testid}-row-${i}`}
        >
          <input
            className="input py-1.5 text-sm"
            value={l.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Label (Personal site)"
            maxLength={40}
            data-testid={`${testid}-label-${i}`}
          />
          <input
            className="input py-1.5 text-sm col-span-2 sm:col-span-1"
            type="url"
            inputMode="url"
            value={l.url}
            onChange={(e) => update(i, { url: e.target.value })}
            placeholder="https://…"
            maxLength={500}
            data-testid={`${testid}-url-${i}`}
          />
          <button
            type="button"
            className="btn-ghost text-sm px-2 self-start row-start-1 col-start-2 sm:col-start-3"
            onClick={() => remove(i)}
            aria-label="Remove link"
            data-testid={`${testid}-remove-${i}`}
          >
            ✕
          </button>
        </div>
      ))}

      <div>
        <button
          type="button"
          className="btn text-sm"
          onClick={add}
          disabled={value.length >= MAX_LINKS}
          data-testid={`${testid}-add`}
        >
          + Add link
        </button>
        {value.length >= MAX_LINKS && (
          <span className="ml-2 text-xs text-muted">Max {MAX_LINKS} links</span>
        )}
      </div>
    </div>
  );
}
